import React, { useCallback, useState, useEffect } from "react";
import { SpaceWrapper } from "../../../components";
import { DropDownInput, Button, InputField } from "@/components";
import { AiOutlineCheck } from "react-icons/ai";
import { BsArrowLeftShort } from "react-icons/bs";
import { useNavigate } from "react-router";
import { useApi, useAlert } from "@/";
import { useSelector } from "react-redux";

const EditSinglePincode = () => {
  const { callApi, loading } = useApi();
  const navigate = useNavigate();
  const { alert, AlertDialog } = useAlert();
  const [pincode, setPincode] = useState("");
  const [pincodeData, setPincodeData] = useState({
    city: "",
    state: "",
    stateCluster: "",
    metro: false,
    serviceLevel: false,
    courierPartners: [],
  });
  const [isFetching, setIsFetching] = useState(false);

  const optionArray = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const couriers = useSelector((state) => state.app.appData.courier) || [];

  const courierOptionsArray = couriers.map((courier) => ({
    value: courier._id,
    label: courier.name,
  }));

  // Handle change for courier partner services
  const handleCourierChange = (courierId, field, value) => {
    setPincodeData((prev) => {
      const updatedPartners = prev.courierPartners.map((partner) => {
        if (partner.courierId?._id === courierId) {
          return { ...partner, [field]: value };
        }
        return partner;
      });
      return { ...prev, courierPartners: updatedPartners };
    });
  };

  // Handle change for basic pincode data
  const handleChange = (key, value) => {
    setPincodeData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFetch = async () => {
    if (!pincode) {
      alert("Please enter pincode");
      return;
    }

    if (!pincode.match(/^[0-9]{6}$/)) {
      alert("Please enter valid 6 digit pincode");
      return;
    }

    try {
      setIsFetching(true);
      const response = await callApi(
        "/admin/getPincodeByPincode",
        "GET",
        null,
        {
          pincode: pincode,
        }
      );
      console.log("Response", response);
      if (!response.success) {
        alert(response.error);
        return;
      }
      console.log("Pincode Data", response.data);
      setPincodeData(response.data);
    } catch (error) {
      alert("Failed to fetch pincode data:- " + error.message);
      console.log(error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await callApi("/admin/updatePincode", "PUT", {
        ...pincodeData,
        pincode: parseInt(pincode),
        courierPartners: pincodeData.courierPartners.map((partner) => ({
          ...partner,
          courierId: partner.courierId?._id,
        })),
      });
      console.log("Response", response);
      if (response.success) {
        alert("Pincode updated successfully", "success");
      } else {
        alert(response.error || "Failed to update pincode");
      }
    } catch (error) {
      alert("Failed to update pincode: " + error.message);
      console.log(error);
    }
  };

  const clusterOptions = [
    { value: "CLUSTER 1", label: "CLUSTER 1" },
    { value: "CLUSTER 2", label: "CLUSTER 2" },
    { value: "CLUSTER 3", label: "CLUSTER 3" },
    { value: "CLUSTER 4", label: "CLUSTER 4" },
    { value: "CLUSTER 5", label: "CLUSTER 5" },
    { value: "CLUSTER 6", label: "CLUSTER 6" },
    { value: "CLUSTER 7", label: "CLUSTER 7" },
    { value: "CLUSTER 8", label: "CLUSTER 8" },
  ];

  // Render courier partner section
  const renderCourierPartnerSection = (courierId) => {
    const partner = pincodeData.courierPartners.find(
      (p) => p.courierId?._id === courierId
    );
    if (!partner) return null;

    return (
      <div
        key={courierId}
        className="flex flex-col gap-4 p-4 border-[1.6px] border-secondary/35 rounded-xl"
      >
        <h2 className="flex items-center gap-1 text-headline5 lg:text-headline4 font-nunito font-bold text-secondary/95">
          {partner.courierId?.name}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4 items-end">
          <DropDownInput
            label="Air Service"
            placeholder="Select Yes/No"
            options={optionArray}
            value={partner.airService}
            onChange={(value) =>
              handleCourierChange(courierId, "airService", value)
            }
          />
          <DropDownInput
            label="Surface Service"
            placeholder="Select Yes/No"
            options={optionArray}
            value={partner.surfaceService}
            onChange={(value) =>
              handleCourierChange(courierId, "surfaceService", value)
            }
          />
          <DropDownInput
            label="Pickup"
            placeholder="Select Yes/No"
            options={optionArray}
            value={partner.pickup}
            onChange={(value) =>
              handleCourierChange(courierId, "pickup", value)
            }
          />
          <DropDownInput
            label="Delivery"
            placeholder="Select Yes/No"
            options={optionArray}
            value={partner.delivery}
            onChange={(value) =>
              handleCourierChange(courierId, "delivery", value)
            }
          />
          <DropDownInput
            label="COD"
            placeholder="Select Yes/No"
            options={optionArray}
            value={partner.codAvailable}
            onChange={(value) =>
              handleCourierChange(courierId, "codAvailable", value)
            }
          />
        </div>
      </div>
    );
  };

  // Loading skeleton for pincode details
  const PincodeDetailsSkeleton = () => (
    <div className="flex flex-col gap-4 p-4 border-[1.6px] border-secondary/35 rounded-xl">
      <h2 className="flex items-center gap-1 text-headline5 lg:text-headline4 font-nunito font-bold text-secondary/95">
        <div className="h-6 w-32 bg-secondary/15 rounded animate-pulse"></div>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 items-end">
        {Array(4)
          .fill(0)
          .map((_, index) => (
            <div key={index} className="flex flex-col gap-1 w-full">
              <div className="h-5 w-20 bg-secondary/15 rounded animate-pulse"></div>
              <div className="h-10 w-full bg-secondary/15 rounded animate-pulse"></div>
            </div>
          ))}
      </div>
    </div>
  );

  // Loading skeleton for courier partner section
  const CourierPartnerSkeleton = () => (
    <div className="flex flex-col gap-4 p-4 border-[1.6px] border-secondary/35 rounded-xl">
      <h2 className="flex items-center gap-1 text-headline5 lg:text-headline4 font-nunito font-bold text-secondary/95">
        <div className="h-6 w-28 bg-secondary/15 rounded animate-pulse"></div>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4 items-end">
        {Array(5)
          .fill(0)
          .map((_, index) => (
            <div key={index} className="flex flex-col gap-1 w-full">
              <div className="h-5 w-20 bg-secondary/15 rounded animate-pulse"></div>
              <div className="h-10 w-full bg-secondary/15 rounded animate-pulse"></div>
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-3 p-4 pb-20 md:pb-4 pt-16 relative max-w-[2000px]">
        <h2 className="flex items-center gap-1.5 text-headline4 lg:text-headline3 font-nunito font-bold text-secondary/95">
          <BsArrowLeftShort
            onClick={() => navigate(-1)}
            className="text-4xl lg:text-5xl text-secondary/70 hover:bg-secondary/5 rounded-xl font-nunito font-bold cursor-pointer"
          />
          Update Specific Pincode
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-2 items-end">
          <InputField
            label="Pincode"
            placeholder="Enter pincode"
            value={pincode}
            onChange={(value) => setPincode(value)}
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleFetch}
              className="h-12 px-6"
              isLoading={loading}
            >
              Fetch
            </Button>
          </div>
        </div>

        {isFetching ? (
          <>
            <PincodeDetailsSkeleton />
            {Array(5)
              .fill(0)
              .map((_, index) => (
                <CourierPartnerSkeleton key={index} />
              ))}
          </>
        ) : pincodeData._id ? (
          <>
            <div className="flex flex-col gap-4 p-4 border-[1.6px] border-secondary/35 rounded-xl">
              <h2 className="flex items-center gap-1 text-headline5 lg:text-headline4 font-nunito font-bold text-secondary/95">
                Pincode - {pincode}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4 items-end">
                <InputField
                  label="City"
                  placeholder="Enter city"
                  value={pincodeData.city}
                  onChange={(value) => handleChange("city", value)}
                  disabled
                />
                <InputField
                  label="State"
                  placeholder="Enter state"
                  value={pincodeData.state}
                  onChange={(value) => handleChange("state", value)}
                  disabled
                />
                <DropDownInput
                  label="State Cluster"
                  placeholder="Enter state cluster"
                  options={clusterOptions}
                  value={pincodeData.stateCluster}
                  onChange={(value) => handleChange("stateCluster", value)}
                />
                <DropDownInput
                  label="Metro"
                  placeholder="Select Yes/No"
                  options={optionArray}
                  value={pincodeData.metro}
                  onChange={(value) => handleChange("metro", value)}
                />
                <DropDownInput
                  label="Special Service"
                  placeholder="Select Yes/No"
                  options={optionArray}
                  value={pincodeData.serviceLevel}
                  onChange={(value) => handleChange("serviceLevel", value)}
                />
              </div>
            </div>

            {/* Render courier partner sections */}
            {courierOptionsArray.map((courier) =>
              renderCourierPartnerSection(courier.value)
            )}

            <div className="flex justify-end items-end">
              <Button
                variant="default"
                size="md"
                className="mt-2"
                icon={AiOutlineCheck}
                onClick={handleSave}
                isLoading={loading}
              >
                Save Changes
              </Button>
            </div>
          </>
        ) : null}
      </div>
      <AlertDialog />
    </SpaceWrapper>
  );
};

export default EditSinglePincode;
